'use client';

import { useCallback, useMemo } from 'react';
import { useStore } from '@/providers/app-store-provider';
import { useReporting } from '@/hooks/useReporting';
import { ProviderType } from '@/reporting/types';
import {
  useCartSensorsTracking,
  type CartRecommendTrackingParams,
  type RecommendProduct,
} from '@/app/cart/hooks/useCartSensorsTracking';
import { env } from '@/utils/env';
import { getProductDisplayLabel, getProductImages, getWishNumber } from '@/utils/goods-list-utils';
import { priceInfo } from '@/utils/price';
import { setBlockFlags } from '@/utils/feature-value';
import { setSession } from '@/utils/tools';
import logger from '@/utils/client-logger';
import type { ListProduct } from '@/api/types/public';
import type { MarketingRuntimeConfig } from '../../../types';
import { getTrackingCommon } from './useSeedingTracking';

const CAMPAIGN_RECOMMEND_COLLECT_SCENE = '营销活动页你也喜欢推荐商品';
const CAMPAIGN_RECOMMEND_REFER_POSITION = '营销活动页你也喜欢推荐商品';

const RECOMMEND_EVENT = {
  CLICK: 'CampaignPageProduct_Click',
  COLLECT_CLICK: 'CampaignPageCollectBtn_Click',
  EXP: 'CampaignPageProduct_Exp',
  MODEL_EXP: 'CampaignPageProduct_ModelExp',
} as const;

const CAMPAIGN_RECOMMEND_MODULE_NAME = '商品版位-你也喜欢';
const CAMPAIGN_RECOMMEND_PRODUCT_MODULE_NAME = '商品推荐版位';
const CAMPAIGN_RECOMMEND_COLLECT_MODULE_NAME = '推荐商品版位';
const CAMPAIGN_RECOMMEND_AD_MODULE_NAME = '商品推荐版位';
const CAMPAIGN_RECOMMEND_TAB_NAME = 'You May Also Like';

type CampaignRecommendProduct = RecommendProduct & {
  sku?: string;
  products_category?: string;
};

/** 活动页推荐商品埋点（分组/算法共用同一套事件与公共字段） */
export function useCampaignRecommendTracking(runtimeConfig: MarketingRuntimeConfig) {
  const { trackEvent } = useReporting();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const commonFields = useMemo(() => getTrackingCommon(runtimeConfig), [runtimeConfig]);

  const { getCartRecommendSensorsData } = useCartSensorsTracking();

  const buildRecommendSensorsData = useCallback(
    (item: ListProduct | RecommendProduct, index: number): CartRecommendTrackingParams => {
      const currencyName = currency?.name || env.NEXT_PUBLIC_DEFAULT_CURRENCY;
      const product = item as RecommendProduct;
      const currentPriceInfo = priceInfo(userInfo, product as any, currency);
      const basePriceInfo = priceInfo(userInfo, product as any, {
        name: env.NEXT_PUBLIC_DEFAULT_CURRENCY || currencyName,
      });
      const { productImage } = getProductImages(product as any);

      return {
        product_index: index,
        shop_sku: item.shop_sku || '',
        business_sku: item.business_sku || '',
        product_sku: item.shop_sku || '',
        product_id: String(item.id || ''),
        product_price: currentPriceInfo.price || '0',
        current_product_price: currentPriceInfo.sell_price || '0',
        base_current_product_price: basePriceInfo.sell_price || '0',
        currency: currencyName,
        recall_group_type: product.recall_group_type || '',
        fine_ranking_type: product.fine_ranking_type || '',
        recommend_result_type: product.recommend_result_type || '',
        product_display_label: getProductDisplayLabel(product as any, 1),
        product_collect_num: Number(getWishNumber(product as any)),
        product_rating: parseFloat((product as any).spu_review_stars || '0'),
        display_color_num: (product as any).correlation?.length || 0,
        first_picture_url: productImage.src,
        product_class_name: (product as any).products_category || '',
        module_name: CAMPAIGN_RECOMMEND_MODULE_NAME,
      };
    },
    [currency, userInfo],
  );

  const buildCampaignRecommendEventParams = useCallback(
    (item: ListProduct | RecommendProduct, index: number) => {
      const currencyName = currency?.name || env.NEXT_PUBLIC_DEFAULT_CURRENCY;
      const product = item as CampaignRecommendProduct;
      const currentPriceInfo = priceInfo(userInfo, product, currency);
      const basePriceInfo = priceInfo(userInfo, product, {
        name: env.NEXT_PUBLIC_DEFAULT_CURRENCY || currencyName,
      });

      return {
        ...commonFields,
        module_name: CAMPAIGN_RECOMMEND_PRODUCT_MODULE_NAME,
        pits_index: index,
        business_sku: item.business_sku || '',
        shop_sku: item.shop_sku || '',
        product_sku: product.sku || item.shop_sku || '',
        product_id: String(item.id || ''),
        product_name: product.short_name || product.name || '',
        product_class_name: product.products_category || '',
        product_price: Number(currentPriceInfo.price || '0'),
        current_product_price: Number(currentPriceInfo.sell_price || '0'),
        base_current_product_price: Number(basePriceInfo.sell_price || '0'),
        dollar_exchange_rate: parseFloat(currency?.exchange_rate || '1'),
        currency: currencyName,
      };
    },
    [commonFields, currency, userInfo],
  );

  const trackRecommendEvent = useCallback(
    (
      eventName: string,
      item: ListProduct | RecommendProduct,
      index: number,
      extraProperties?: Record<string, any>,
    ) => {
      try {
        const eventParams = {
          ...buildCampaignRecommendEventParams(item, index),
          ...extraProperties,
        };

        trackEvent(
          {
            eventName,
            properties: eventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error(`${eventName} tracking error:`, error);
      }
    },
    [buildCampaignRecommendEventParams, trackEvent],
  );

  const trackRecommendClick = useCallback(
    (item: ListProduct | RecommendProduct, index: number) => {
      trackRecommendEvent(RECOMMEND_EVENT.CLICK, item, index, {
        tab_name: CAMPAIGN_RECOMMEND_TAB_NAME,
      });

      const sensorsData = buildRecommendSensorsData(item, index);
      const eventParams = getCartRecommendSensorsData(sensorsData, item as RecommendProduct);
      setBlockFlags({
        block_flags: eventParams.block_flags,
        block_flags_ext: eventParams.block_flags_ext,
        fine_ranking_type: eventParams.fine_ranking_type,
        recall_group_type: eventParams.recall_group_type,
        recommend_result_type: eventParams.recommend_result_type || '',
        product_index: eventParams.product_index,
        first_picture_url: eventParams.first_picture_url,
        cart_item_shop_sku_list: eventParams.cart_item_shop_sku_list,
        cart_item_product_id_list: eventParams.cart_item_product_id_list.map(String),
        refer_page_position: CAMPAIGN_RECOMMEND_REFER_POSITION,
      });
      setSession('zl_goodsDetailEnterPageScene', CAMPAIGN_RECOMMEND_REFER_POSITION);
    },
    [buildRecommendSensorsData, getCartRecommendSensorsData, trackRecommendEvent],
  );

  const buildRecommendAdProperties = useCallback(
    (item: ListProduct | RecommendProduct, index: number) => {
      const product = item as CampaignRecommendProduct & {
        Jump_target?: string | number | null;
        jump_type?: string | number | null;
        jump_value?: string | number | null;
        type?: string | number | null;
      };
      const jumpType = product.type ?? product.jump_type ?? '';
      const jumpContent = product.Jump_target ?? product.jump_value ?? product.id ?? '';

      return {
        ...commonFields,
        module_name: CAMPAIGN_RECOMMEND_AD_MODULE_NAME,
        pits_index: index,
        pits_jump_type: jumpType == null ? '' : String(jumpType),
        pits_jump_content: jumpContent == null ? '' : String(jumpContent),
      };
    },
    [commonFields],
  );

  const trackRecommendAdExposure = useCallback(
    (item: ListProduct | RecommendProduct, index: number) => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPageAD_Exp',
            properties: buildRecommendAdProperties(item, index),
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('CampaignPageAD_Exp tracking error:', error);
      }
    },
    [buildRecommendAdProperties, trackEvent],
  );

  const trackRecommendAdClick = useCallback(
    (item: ListProduct | RecommendProduct, index: number) => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPageAD_Click',
            properties: buildRecommendAdProperties(item, index),
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('CampaignPageAD_Click tracking error:', error);
      }
    },
    [buildRecommendAdProperties, trackEvent],
  );

  const trackRecommendCollectClick = useCallback(
    (item: ListProduct | RecommendProduct, index: number, collectType?: string) => {
      trackRecommendEvent(RECOMMEND_EVENT.COLLECT_CLICK, item, index, {
        module_name: CAMPAIGN_RECOMMEND_COLLECT_MODULE_NAME,
        collect_type: collectType || '',
      });
    },
    [trackRecommendEvent],
  );

  const trackRecommendValidExp = useCallback(
    (item: ListProduct | RecommendProduct, index: number) => {
      // 二期专题页只升级曝光判定机制，事件名继续沿用 CampaignPageProduct_Exp。
      trackRecommendEvent(RECOMMEND_EVENT.EXP, item, index, {
        tab_name: CAMPAIGN_RECOMMEND_TAB_NAME,
      });
    },
    [trackRecommendEvent],
  );

  const trackRecommendModelExp = useCallback(() => {
    logger.info('trackRecommendModelExp');
    // try {
    //   trackEvent(
    //     {
    //       eventName: RECOMMEND_EVENT.MODEL_EXP,
    //       properties: {
    //         ...commonFields,
    //       },
    //     },
    //     [ProviderType.SENSORS],
    //   );
    // } catch (error) {
    //   logger.error(`${RECOMMEND_EVENT.MODEL_EXP} tracking error:`, error);
    // }
  }, []);

  return {
    buildRecommendSensorsData,
    trackRecommendClick,
    trackRecommendAdExposure,
    trackRecommendAdClick,
    trackRecommendCollectClick,
    trackRecommendValidExp,
    trackRecommendModelExp,
    collectScene: CAMPAIGN_RECOMMEND_COLLECT_SCENE,
  };
}
